import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { cartoesApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { ModalidadeCartao } from '../../lib/enums'

const NOMES_MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

/** Botão + modal pra marcar um lançamento real (ex.: pagamento parcial de fatura importado da Pluggy) como
 * ajuste de uma fatura do cartão: nasce um lançamento de ajuste novo, ligado à fatura, sem tocar no original. */
export function BotaoAjusteFatura({ transacaoId, onAjustado }: { transacaoId: string; onAjustado: () => void }) {
  const [aberto, setAberto] = useState(false)
  const [cartaoId, setCartaoId] = useState('')
  const [faturaId, setFaturaId] = useState('')
  const toast = useToast()

  const cartoes = useQuery({ queryKey: ['cartoes'], queryFn: () => cartoesApi.list() })
  const cartoesCredito = (cartoes.data ?? []).filter((c) => c.modalidade === ModalidadeCartao.Credito)

  const faturas = useQuery({
    queryKey: ['faturas', cartaoId],
    queryFn: () => cartoesApi.listFaturas(cartaoId),
    enabled: !!cartaoId,
  })

  const ajustar = useMutation({
    mutationFn: () => cartoesApi.marcarAjusteFatura(transacaoId, cartaoId, faturaId || undefined),
    onSuccess: () => { toast.success('Ajuste lançado na fatura.'); setAberto(false); onAjustado() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <Button type="button" variant="light" icon="ri-git-commit-line" onClick={() => setAberto(true)}>Ajuste de fatura</Button>
      <Modal open={aberto} onClose={() => setAberto(false)} title="Ajuste de fatura">
        <p className="mb-3 text-13 text-muted">
          Use pra um pagamento parcial da fatura ou um estorno já refletido nesse lançamento. Nasce um lançamento de
          ajuste ligado à fatura, reduzindo o total dela — este lançamento não é alterado.
        </p>
        <div className="space-y-3">
          <Select label="Cartão" value={cartaoId} onChange={(e) => { setCartaoId(e.target.value); setFaturaId('') }}>
            <option value="">Escolher cartão</option>
            {cartoesCredito.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </Select>
          {cartaoId && (
            <Select label="Fatura" value={faturaId} onChange={(e) => setFaturaId(e.target.value)}>
              <option value="">Automática (pela data do lançamento)</option>
              {(faturas.data ?? []).map((f) => (
                <option key={f.id} value={f.id}>{NOMES_MES[f.mes - 1]} de {f.ano}</option>
              ))}
            </Select>
          )}
          <Button type="button" className="w-full" disabled={!cartaoId} loading={ajustar.isPending} onClick={() => ajustar.mutate()}>
            Lançar ajuste
          </Button>
        </div>
      </Modal>
    </>
  )
}
